import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import type { PaginationMeta, SortDirection } from "./usePagination";

import {
  DEFAULT_PAGINATION,
  generatePaginationKey,
  getPagination,
  setPagination,
} from "@/utils/indexedDB/paginationDB";

const EXCLUDED_PAGINATION_QUERY_KEYS = new Set(["totalPages", "totalResult"]);

type UsePersistedPaginationReturnProps = {
  localPagination: PaginationMeta;
  setLocalPagination: React.Dispatch<React.SetStateAction<PaginationMeta>>;
  resetToFirstPage: () => void;
  paginationQuery: string;
  isLoading: boolean;
  storageKey: string;
};

const buildPaginationQuery = (
  paginationData: PaginationMeta | undefined,
): string => {
  if (!paginationData) {
    return "";
  }

  const params = Object.entries(paginationData).flatMap(([key, value]) => {
    if (
      EXCLUDED_PAGINATION_QUERY_KEYS.has(key) ||
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return [];
    }

    if (typeof value === "number" && !Number.isFinite(value)) {
      return [];
    }

    return [`${key}=${encodeURIComponent(String(value))}`];
  });

  return params.length ? `&${params.join("&")}` : "";
};

/**
 * Owns persisted table pagination state and mirrors pagination params to the URL.
 *
 * Module-switch reset is handled in the sidebar (SidebarItem clears IndexedDB
 * entries for the previous module on cross-module clicks). This hook is
 * therefore responsible only for loading whatever entry exists for the current
 * storageKey and exposing setLocalPagination / resetToFirstPage so feature
 * pages can trigger their own resets (filter change, successful create, etc.).
 *
 * @param {number} limit - Default page size.
 * @param {string} activeTab - Optional active tab identifier for tab-specific tables.
 * @param {string} tableId - Optional table identifier for pages with multiple tables.
 * @param {object} [options] - Configuration options.
 * @param {boolean} [options.enabled] - Whether pagination should load, persist, and sync URL params.
 * @param {object} [options.defaultSort] - Tab-specific default sort used when no persisted sort exists.
 * @param {string} [options.defaultSort.sortBy] - Default sort field.
 * @param {string} [options.defaultSort.orderBy] - Default sort direction.
 * @returns {UsePersistedPaginationReturnProps} Persisted pagination state, setter, query string, and loading state.
 */
const usePersistedPagination = (
  limit?: number,
  activeTab?: string,
  tableId?: string,
  options?: {
    enabled?: boolean;
    defaultSort?: { sortBy?: string; orderBy?: SortDirection };
  },
): UsePersistedPaginationReturnProps => {
  const { enabled = true, defaultSort } = options ?? {};
  const location = useLocation();
  const defaultSortBy = defaultSort?.sortBy;
  const defaultOrderBy = defaultSort?.orderBy;
  const defaultPagination = useMemo<PaginationMeta>(
    () => ({
      ...DEFAULT_PAGINATION,
      limit: limit ?? DEFAULT_PAGINATION.limit,
      sortBy: defaultSortBy,
      orderBy: defaultOrderBy as SortDirection | undefined,
    }),
    [limit, defaultSortBy, defaultOrderBy],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [localPagination, setLocalPaginationState] =
    useState<PaginationMeta>(defaultPagination);
  const storageKey = useMemo(
    () => generatePaginationKey(location.pathname, activeTab, tableId),
    [activeTab, location.pathname, tableId],
  );
  // Track the storageKey we have actually finished loading for. When the key
  // changes (tab switch, navigation), isLoading becomes true synchronously in
  // the same render so callers that gate their RTK Query on shouldSkipQuery
  // do not fire a stale request with the previous key's pagination state
  // before the async getPagination resolves.
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);
  const isKeyResolved = enabled ? resolvedKey === storageKey : true;

  useEffect(() => {
    let isMounted = true;

    const loadPagination = async (): Promise<void> => {
      if (!enabled) {
        setIsLoading(false);

        return;
      }

      setIsLoading(true);

      try {
        const persistedPagination = await getPagination(storageKey);
        const resolvedPagination: PaginationMeta = {
          ...defaultPagination,
          ...(persistedPagination || {}),
        };

        if (isMounted) {
          setLocalPaginationState(resolvedPagination);
        }
      } catch {
        if (isMounted) {
          setLocalPaginationState({ ...defaultPagination });
        }
      } finally {
        if (isMounted) {
          setResolvedKey(storageKey);
          setIsLoading(false);
        }
      }
    };

    void loadPagination();

    return (): void => {
      isMounted = false;
    };
  }, [
    activeTab,
    defaultPagination,
    enabled,
    location.pathname,
    storageKey,
    tableId,
  ]);

  const isDefaultState = useCallback(
    (pagination: PaginationMeta): boolean => {
      return (
        (defaultSort
          ? pagination.sortBy === defaultSort.sortBy &&
            pagination.orderBy === defaultSort.orderBy
          : !("sortBy" in pagination) && !("orderBy" in pagination)) &&
        !("search" in pagination) &&
        pagination.currentPage === defaultPagination.currentPage &&
        pagination.offset === defaultPagination.offset &&
        pagination.limit === defaultPagination.limit
      );
    },
    [
      defaultPagination.currentPage,
      defaultPagination.limit,
      defaultPagination.offset,
      defaultSort,
    ],
  );

  const setLocalPagination = useCallback<
    React.Dispatch<React.SetStateAction<PaginationMeta>>
  >(
    (action) => {
      setLocalPaginationState((previousPagination) => {
        const nextPagination =
          typeof action === "function" ? action(previousPagination) : action;

        if (enabled && !isDefaultState(nextPagination)) {
          void setPagination(storageKey, nextPagination);
        }

        return nextPagination;
      });
    },
    [enabled, isDefaultState, storageKey],
  );

  const resetToFirstPage = useCallback((): void => {
    setLocalPagination(() => ({
      ...defaultPagination,
    }));
  }, [defaultPagination, setLocalPagination]);

  const paginationQuery = buildPaginationQuery(localPagination);

  return {
    localPagination,
    setLocalPagination,
    resetToFirstPage,
    paginationQuery,
    isLoading: isLoading || !isKeyResolved,
    storageKey,
  };
};

export default usePersistedPagination;
